import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import moment from 'moment';
import { SessionSchedulePicker } from './SessionSchedulePicker';

interface EditSessionDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (startNow: boolean, scheduledDate: Date | null, disableAllUsers: boolean) => void;
  scheduledDate: Date | null;
  isEditingEndDate?: boolean;
  activeSessionStartDate?: Date | null;
  scheduledStartDate?: Date | null;
}

const IMMEDIATE = "startNow";
const SCHEDULED = "schedule";

const EditSessionDateModal: React.FC<EditSessionDateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  scheduledDate,
  isEditingEndDate,
  activeSessionStartDate,
  scheduledStartDate,
}) => {
  const [immediateOrScheduled, setImmediateOrScheduled] = useState(
    scheduledDate ? SCHEDULED : IMMEDIATE
  );
  const [userScheduledDate, setUserScheduledDate] = useState<Date | null>(
    scheduledDate
  );
  const [error, setError] = useState<string | null>(null);
  const [showDisableOption, setShowDisableOption] = useState(false);
  const [disableAllUsers, setDisableAllUsers] = useState(false);

  useEffect(() => {
    setImmediateOrScheduled(scheduledDate ? SCHEDULED : IMMEDIATE);
    setUserScheduledDate(scheduledDate);
    setError(null);
    setShowDisableOption(false);
    setDisableAllUsers(false);
  }, [scheduledDate, isOpen]);

  const handleClose = () => {
    onClose();
    setImmediateOrScheduled(IMMEDIATE);
    setUserScheduledDate(new Date());
    setError(null);
    setShowDisableOption(false);
    setDisableAllUsers(false);
  };

  const handleConfirm = () => {
    // Phase 1: Validate and show disable option (only for end date editing)
    if (!showDisableOption && isEditingEndDate) {
      const now = new Date();

      if (immediateOrScheduled !== IMMEDIATE && userScheduledDate) {
        if (scheduledStartDate && userScheduledDate <= scheduledStartDate) {
          setError('End date must be after the scheduled start date.');
          return;
        }

        if (activeSessionStartDate && userScheduledDate <= activeSessionStartDate) {
          setError('End date must be after the active session start date.');
          return;
        }

        if (userScheduledDate <= now) {
          setError('Scheduled end date must be in the future.');
          return;
        }
      } else if (immediateOrScheduled === IMMEDIATE) {
        if (scheduledStartDate && scheduledStartDate >= now) {
          setError('Cannot end session that has not started.');
          return;
        }
      }

      // Validation passed, show the disable option
      setShowDisableOption(true);
      setError(null);
      return;
    }

    // Phase 2: Final confirm - call onConfirm with all parameters
    if (isEditingEndDate) {
      onConfirm(immediateOrScheduled === IMMEDIATE, userScheduledDate, disableAllUsers);
    } else {
      // For start dates, disableAllUsers is not applicable, pass false
      onConfirm(immediateOrScheduled === IMMEDIATE, userScheduledDate, false);
    }
    
    // Reset state
    setImmediateOrScheduled(IMMEDIATE);
    setUserScheduledDate(new Date());
    setShowDisableOption(false);
    setDisableAllUsers(false);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>
        {isEditingEndDate ? 'Configure Course End' : 'Schedule Start Date'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: 0, width: '340px' }}>
        <SessionSchedulePicker
          modeLabelNow={isEditingEndDate ? 'End Now' : 'Start Now'}
          modeLabelSchedule={isEditingEndDate ? 'Schedule End' : 'Schedule Start'}
          scheduledDate={userScheduledDate}
          onChange={(startNow, date) => {
            setImmediateOrScheduled(startNow ? IMMEDIATE : SCHEDULED);
            setUserScheduledDate(date);
            setError(null);
          }}
          error={error}
          setError={setError}
        />
        {isEditingEndDate && (
          <Collapse in={showDisableOption}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={disableAllUsers}
                  onChange={(e) => setDisableAllUsers(e.target.checked)}
                />
              }
              label="Disable all users when (or after) course ends?"
              sx={{ marginTop: 2 }}
            />
            <DialogContentText sx={{ marginLeft: 4, marginTop: 0, fontSize: '0.875rem' }}>
              Disabled users will not be deleted nor will they be removed from the gradebook for this course.
            </DialogContentText>
          </Collapse>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>CANCEL</Button>
        <Button type="submit" onClick={handleConfirm}>CONFIRM</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSessionDateModal;
